import '../financials/financialsResponsive.css';
// BL-173 Gate 2 — standalone "add a card" form.
// Spec: IntelAgent_BL173_Payments_Handling_Collection_v1_00.md (§5 Gate 2, §6 Gate 3).
//
// Deliberately self-contained and usable in isolation: Gate 3b builds the actual card
// list/management surface and wires this into a Module 9 screen, so nothing here reaches
// for app routing, nav or shared layout.
//
// The card number is collected by Stripe's own hosted Elements iframe and tokenized
// client-side — it never touches IntelAgent's servers. We hold a SetupIntent
// client_secret and, on success, a PaymentMethod id, nothing more. The resulting
// payment_methods row is written by the stripe-setup-intent Edge Function (service role),
// not by this component: payment_methods RLS is staff-only, so a portal user has no
// direct write path.

import { useCallback, useEffect, useRef, useState } from 'react';
import { supabaseClient } from '../../config/supabase';

const STRIPE_JS_URL = 'https://js.stripe.com/v3';
const EDGE_FUNCTION = 'stripe-setup-intent';

const USER_LABELS = [
    { value: 'personal', text: 'Personal' },
    { value: 'business', text: 'Business' }
];

// Loads Stripe.js from Stripe's own domain (a PCI requirement — it may not be bundled)
// and hands back the Stripe instance for the publishable key.
export function loadStripeJs(publishableKey, { documentRef = typeof document === 'undefined' ? null : document } = {}) {
    if (!publishableKey) {
        return Promise.reject(new Error('Stripe publishable key is not configured.'));
    }
    if (!documentRef) {
        return Promise.reject(new Error('Stripe.js can only be loaded in a browser.'));
    }
    if (typeof window !== 'undefined' && window.Stripe) {
        return Promise.resolve(window.Stripe(publishableKey));
    }

    return new Promise((resolve, reject) => {
        const existing = documentRef.querySelector(`script[src="${STRIPE_JS_URL}"]`);
        const script = existing || documentRef.createElement('script');

        const onLoad = () => {
            if (typeof window === 'undefined' || !window.Stripe) {
                reject(new Error('Stripe.js loaded but did not initialize.'));
                return;
            }
            resolve(window.Stripe(publishableKey));
        };

        script.addEventListener('load', onLoad, { once: true });
        script.addEventListener('error', () => reject(new Error('Stripe.js failed to load.')), { once: true });

        if (!existing) {
            script.src = STRIPE_JS_URL;
            script.async = true;
            documentRef.head.appendChild(script);
        }
    });
}

export default function AddCardForm({
    ownerType = 'user',
    portalAccountId = null,
    accountId = null,
    publishableKey = process.env.REACT_APP_STRIPE_PUBLISHABLE_KEY,
    client = supabaseClient,
    stripeLoader = loadStripeJs,
    onSaved = null
}) {
    const cardMountRef = useRef(null);
    const stripeRef = useRef(null);
    const cardElementRef = useRef(null);

    const isCompanyCard = ownerType === 'account';
    const [label, setLabel] = useState(isCompanyCard ? 'company' : 'personal');
    const [isDefault, setIsDefault] = useState(false);
    const [ready, setReady] = useState(false);
    const [submitting, setSubmitting] = useState(false);
    const [error, setError] = useState('');
    const [savedCard, setSavedCard] = useState(null);

    useEffect(() => {
        let cancelled = false;

        stripeLoader(publishableKey)
            .then((stripe) => {
                if (cancelled || !cardMountRef.current) return;
                stripeRef.current = stripe;
                const elements = stripe.elements();
                const card = elements.create('card', { hidePostalCode: false, disableLink: true });
                card.mount(cardMountRef.current);
                cardElementRef.current = card;
                setReady(true);
            })
            .catch((loadError) => {
                if (cancelled) return;
                setError(loadError.message || 'Stripe could not be loaded.');
            });

        return () => {
            cancelled = true;
            if (cardElementRef.current?.destroy) {
                cardElementRef.current.destroy();
                cardElementRef.current = null;
            }
        };
    }, [publishableKey, stripeLoader]);

    const invoke = useCallback(
        async (payload) => {
            const { data, error: invokeError } = await client.functions.invoke(EDGE_FUNCTION, { body: payload });
            if (invokeError) {
                throw new Error(invokeError.message || 'The card service could not be reached.');
            }
            if (data?.error) {
                throw new Error(data.error);
            }
            return data;
        },
        [client]
    );

    const handleSubmit = useCallback(
        async (event) => {
            event.preventDefault();
            if (submitting) return;

            setError('');
            setSavedCard(null);

            const stripe = stripeRef.current;
            const card = cardElementRef.current;
            if (!stripe || !card) {
                setError('The card field is not ready yet.');
                return;
            }

            setSubmitting(true);
            const owner = {
                ownerType,
                portalAccountId: ownerType === 'user' ? portalAccountId : null,
                accountId: ownerType === 'account' ? accountId : null,
                label
            };

            try {
                const intent = await invoke({ action: 'create_setup_intent', ...owner });
                if (!intent?.clientSecret) {
                    throw new Error('No SetupIntent was returned.');
                }

                // Stripe tokenizes and confirms client-side; we only ever see the resulting id.
                const confirmation = await stripe.confirmCardSetup(intent.clientSecret, {
                    payment_method: { card }
                });

                if (confirmation?.error) {
                    throw new Error(confirmation.error.message || 'Stripe declined this card.');
                }

                const paymentMethodId = confirmation?.setupIntent?.payment_method;
                if (!paymentMethodId) {
                    throw new Error('Stripe did not return a saved payment method.');
                }

                const saved = await invoke({
                    action: 'save_payment_method',
                    ...owner,
                    paymentMethodId,
                    isDefault
                });

                if (cardElementRef.current?.clear) {
                    cardElementRef.current.clear();
                }
                setSavedCard(saved?.paymentMethod ?? null);
                if (onSaved) onSaved(saved?.paymentMethod ?? null);
            } catch (submitError) {
                setError(submitError.message || 'That card could not be saved.');
            } finally {
                setSubmitting(false);
            }
        },
        [accountId, invoke, isDefault, label, onSaved, ownerType, portalAccountId, submitting]
    );

    return (
        <form onSubmit={handleSubmit} className="ia-financials ia-card-form max-w-md space-y-4 rounded-lg border border-gray-200 bg-white p-4">
            <div>
                <h3 className="text-base font-semibold text-gray-900">
                    {isCompanyCard ? 'Add the company card' : 'Add a card'}
                </h3>
                <p className="mt-1 text-sm text-gray-600">
                    Card details are entered directly into Stripe and never stored on our servers.
                </p>
            </div>

            {!isCompanyCard && (
                <fieldset>
                    <legend className="text-sm font-medium text-gray-700">This card is</legend>
                    <div className="mt-2 flex gap-4">
                        {USER_LABELS.map((option) => (
                            <label key={option.value} className="flex items-center gap-2 text-sm text-gray-700">
                                <input
                                    type="radio"
                                    name="card-label"
                                    value={option.value}
                                    checked={label === option.value}
                                    onChange={() => setLabel(option.value)}
                                />
                                {option.text}
                            </label>
                        ))}
                    </div>
                </fieldset>
            )}

            <div>
                <span className="block text-sm font-medium text-gray-700">Card details</span>
                <div
                    ref={cardMountRef}
                    data-testid="stripe-card-element"
                    className="mt-1 rounded-md border border-gray-300 p-3"
                />
            </div>

            <label className="flex items-center gap-2 text-sm text-gray-700">
                <input type="checkbox" checked={isDefault} onChange={(event) => setIsDefault(event.target.checked)} />
                Use this card by default
            </label>

            {error && (
                <p role="alert" className="text-sm text-red-600">
                    {error}
                </p>
            )}

            {savedCard && (
                <p role="status" className="text-sm text-green-700">
                    {`Saved ${savedCard.brand || 'card'} ending ${savedCard.last4 || '••••'}.`}
                </p>
            )}

            <button
                type="submit"
                disabled={!ready || submitting}
                className="rounded-md bg-[var(--btn-fill)] px-4 py-2 text-sm font-medium text-[var(--btn-ink)] disabled:opacity-50"
            >
                {submitting ? 'Saving…' : 'Save card'}
            </button>
        </form>
    );
}
